import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Layers, Users, Briefcase, Image, Star, MessageSquare, Link2, Settings,
  Eye, TrendingUp, TrendingDown, Clock, RefreshCw, BarChart3, Activity, 
  ArrowUp, ArrowDown, Calendar, Globe, FileText, Zap, Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

// Composant de tooltip personnalisé en français
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-sm text-cyan-600">
          <span className="font-semibold">{payload[0].value}</span> visites
        </p>
      </div>
    );
  }
  return null;
};

const CMSDashboard = () => {
  const [stats, setStats] = useState({
    serviceFamilies: 0,
    solutions: 0,
    team: 0,
    portfolio: 0,
    partners: 0,
    testimonials: 0
  });
  const [analytics, setAnalytics] = useState(null);
  const [gameTracking, setGameTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('week'); // 'today', 'week', 'month'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cmsRes, analyticsRes, gameTrackingRes] = await Promise.all([
        api.get('/cms/all'),
        api.get('/cms/analytics/stats').catch(() => ({ data: null })),
        api.get(`/game/cms/tracking/stats?token=${token}`).catch(() => ({ data: null }))
      ]);
      
      setStats({
        serviceFamilies: cmsRes.data.service_families?.length || 0,
        solutions: cmsRes.data.solutions?.length || 0,
        team: cmsRes.data.team?.length || 0,
        portfolio: cmsRes.data.portfolio?.length || 0,
        partners: cmsRes.data.partners?.length || 0,
        testimonials: cmsRes.data.testimonials?.length || 0
      });
      
      if (analyticsRes.data) {
        setAnalytics(analyticsRes.data);
      }
      
      if (gameTrackingRes.data) {
        setGameTracking(gameTrackingRes.data);
      }
    } catch (error) {
      console.error('Erreur de chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickLinks = [
    { to: '/cms/hero', icon: Layers, label: 'Section Hero', desc: 'Titre, sous-titre, CTA', color: 'from-cyan-500 to-blue-600' },
    { to: '/cms/services', icon: Briefcase, label: 'Services', desc: `${stats.serviceFamilies} familles, ${stats.solutions} solutions`, color: 'from-purple-500 to-violet-600' },
    { to: '/cms/team', icon: Users, label: 'Équipe', desc: `${stats.team} membres`, color: 'from-pink-500 to-rose-600' },
    { to: '/cms/portfolio', icon: Image, label: 'Réalisations', desc: `${stats.portfolio} projets`, color: 'from-orange-500 to-amber-600' },
    { to: '/cms/partners', icon: Star, label: 'Partenaires', desc: `${stats.partners} logos`, color: 'from-emerald-500 to-green-600' },
    { to: '/cms/testimonials', icon: MessageSquare, label: 'Témoignages', desc: `${stats.testimonials} avis`, color: 'from-blue-500 to-indigo-600' },
  ];

  // Données du graphique selon la période sélectionnée
  const getChartData = () => {
    if (!analytics) return [];
    switch (chartPeriod) {
      case 'today':
        return analytics.hourly_views || [];
      case 'week':
        return analytics.daily_views || [];
      case 'month':
        return analytics.monthly_views || [];
      default:
        return analytics.daily_views || [];
    }
  };

  const getChartXKey = () => {
    switch (chartPeriod) {
      case 'today': return 'hour';
      case 'week': return 'day_name';
      case 'month': return 'date';
      default: return 'day_name';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-slate-500 mt-1">Gérez le contenu de votre site vitrine</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
          <Button asChild className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
            <Link to="/site" target="_blank">
              <Eye className="w-4 h-4 mr-2" />
              Voir le site
            </Link>
          </Button>
        </div>
      </div>

      {/* Grille de statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.serviceFamilies}</div>
            <p className="text-cyan-100 text-sm">Familles de services</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.solutions}</div>
            <p className="text-purple-100 text-sm">Solutions</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-pink-500 to-rose-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.team}</div>
            <p className="text-pink-100 text-sm">Membres d'équipe</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-amber-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.portfolio}</div>
            <p className="text-orange-100 text-sm">Réalisations</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.partners}</div>
            <p className="text-emerald-100 text-sm">Partenaires</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.testimonials}</div>
            <p className="text-blue-100 text-sm">Témoignages</p>
          </CardContent>
        </Card>
      </div>

      {/* Section Statistiques du Jeu Le Coffre MSC */}
      {gameTracking && (
        <Card className="bg-gradient-to-br from-red-950 to-black border-red-500/30 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Lock className="w-5 h-5" />
              🔐 Le Coffre MSC - Statistiques de visites
            </CardTitle>
            <CardDescription className="text-white/60">
              Nombre de personnes ayant visité la page du jeu via l'icône coffre
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-red-900/30 rounded-lg p-4 text-center border border-red-500/20">
                <p className="text-sm text-red-300">Aujourd'hui</p>
                <p className="text-3xl font-bold text-white">{gameTracking.today?.['coffre-msc'] || 0}</p>
                <p className="text-xs text-white/50">visites</p>
              </div>
              <div className="bg-red-900/30 rounded-lg p-4 text-center border border-red-500/20">
                <p className="text-sm text-red-300">7 derniers jours</p>
                <p className="text-3xl font-bold text-white">{gameTracking.last_7_days?.['coffre-msc'] || 0}</p>
                <p className="text-xs text-white/50">visites</p>
              </div>
              <div className="bg-red-900/30 rounded-lg p-4 text-center border border-red-500/20">
                <p className="text-sm text-red-300">Total</p>
                <p className="text-3xl font-bold text-white">{gameTracking.total?.['coffre-msc'] || 0}</p>
                <p className="text-xs text-white/50">visites</p>
              </div>
            </div>
            
            {/* Mini graphique des 7 derniers jours */}
            {gameTracking.daily_breakdown && gameTracking.daily_breakdown.length > 0 && (
              <div className="mt-4 pt-4 border-t border-red-500/20">
                <p className="text-sm text-red-300 mb-3">Évolution sur 7 jours</p>
                <div className="h-[100px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={gameTracking.daily_breakdown}>
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 10, fill: '#ef4444' }}
                        tickFormatter={(val) => {
                          const d = new Date(val);
                          return d.toLocaleDateString('fr-FR', { weekday: 'short' });
                        }}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1c1917', border: '1px solid #ef4444', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#ef4444' }}
                        formatter={(value) => [value, 'Visites']}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('fr-FR')}
                      />
                      <Bar dataKey="coffre-msc" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section Analytics avec graphique en courbe */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-500" />
            Statistiques de visites
          </h2>
          <Badge variant="outline" className="text-xs">
            <Activity className="w-3 h-3 mr-1" />
            Temps réel
          </Badge>
        </div>
        
        {/* Cartes de statistiques de visites */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.today_views || 0}</p>
                  {analytics?.daily_growth !== undefined && (
                    <div className={cn(
                      "flex items-center text-xs mt-1",
                      analytics.daily_growth >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {analytics.daily_growth >= 0 ? (
                        <TrendingUp className="w-3 h-3 mr-1" />
                      ) : (
                        <TrendingDown className="w-3 h-3 mr-1" />
                      )}
                      {analytics.daily_growth >= 0 ? '+' : ''}{analytics.daily_growth}% vs hier
                    </div>
                  )}
                </div>
                <div className="p-2 bg-cyan-100 rounded-full">
                  <Eye className="w-5 h-5 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Cette semaine</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.week_views || 0}</p>
                  {analytics?.weekly_growth !== undefined && (
                    <div className={cn(
                      "flex items-center text-xs mt-1",
                      analytics.weekly_growth >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {analytics.weekly_growth >= 0 ? (
                        <TrendingUp className="w-3 h-3 mr-1" />
                      ) : (
                        <TrendingDown className="w-3 h-3 mr-1" />
                      )}
                      {analytics.weekly_growth >= 0 ? '+' : ''}{analytics.weekly_growth}% vs sem. préc.
                    </div>
                  )}
                </div>
                <div className="p-2 bg-purple-100 rounded-full">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ce mois</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.month_views || 0}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-full">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.total_views || 0}</p>
                </div>
                <div className="p-2 bg-orange-100 rounded-full">
                  <Globe className="w-5 h-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Graphique en courbe principal */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-500" />
                Évolution des visites
              </CardTitle>
              <Tabs value={chartPeriod} onValueChange={setChartPeriod} className="w-auto">
                <TabsList className="h-8">
                  <TabsTrigger value="today" className="text-xs px-3">Aujourd'hui</TabsTrigger>
                  <TabsTrigger value="week" className="text-xs px-3">7 jours</TabsTrigger>
                  <TabsTrigger value="month" className="text-xs px-3">30 jours</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getChartData()} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey={getChartXKey()} 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    fill="url(#colorViews)"
                    dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Ligne avec Pages populaires et activité récente */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Pages les plus visitées */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                Pages les plus visitées
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(analytics?.top_pages || []).slice(0, 5).map((page, idx) => {
                  const maxViews = Math.max(...(analytics?.top_pages || []).map(p => p.views), 1);
                  const percentage = (page.views / maxViews) * 100;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate max-w-[200px]">
                          {page.page === '/' ? 'Page d\'accueil' : 
                           page.page === '/site' ? 'Accueil' :
                           page.page.replace('/site/', '').replace(/-/g, ' ').charAt(0).toUpperCase() + 
                           page.page.replace('/site/', '').replace(/-/g, ' ').slice(1) || page.page}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {page.views} {page.views > 1 ? 'vues' : 'vue'}
                        </Badge>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-violet-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {(!analytics?.top_pages || analytics.top_pages.length === 0) && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Aucune donnée disponible
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activité récente */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Activité récente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {(analytics?.recent_views || []).slice(0, 8).map((view, idx) => {
                  const date = new Date(view.timestamp);
                  const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                  const pageName = view.page === '/' ? 'Accueil' : 
                                   view.page === '/site' ? 'Accueil' :
                                   view.page.replace('/site/', '').replace(/-/g, ' ');
                  return (
                    <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm text-slate-600 truncate max-w-[150px]">
                          {pageName.charAt(0).toUpperCase() + pageName.slice(1)}
                        </span>
                      </div>
                      <span className="text-xs text-slate-400">{timeStr}</span>
                    </div>
                  );
                })}
                {(!analytics?.recent_views || analytics.recent_views.length === 0) && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Aucune visite récente
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Accès rapide */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Accès rapide</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map(link => {
            const Icon = link.icon;
            return (
              <Link key={link.to} to={link.to}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${link.color} text-white group-hover:scale-110 transition-transform`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{link.label}</h3>
                        <p className="text-sm text-slate-500">{link.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Autres sections utiles */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Actions rapides</CardTitle>
            <CardDescription>Gérez les paramètres clés de votre site</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/cms/schedule">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Modifier les horaires</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
            <Link to="/cms/settings">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Paramètres du site</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
            <Link to="/cms/seo-generator">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Générateur SEO</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ressources</CardTitle>
            <CardDescription>Liens et outils utiles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/cms/media">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Image className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Médiathèque</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
            <Link to="/cms/brochure">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Générer la brochure PDF</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
            <Link to="/cms/requests">
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-slate-500" />
                  <span className="text-sm">Demandes de contact</span>
                </div>
                <ArrowUp className="w-4 h-4 text-slate-400 rotate-90" />
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CMSDashboard;
